import { Box, CircularProgress, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useStateProductTrend } from '../api/depletions';
import { fmt6PkNumber } from '../lib/volume';
import { colors } from '../theme';

// Per-product identity colors (the panel header dot).
const PRODUCT_PALETTE = [
  '#4f46e5',
  '#0d9488',
  '#d97706',
  '#db2777',
  '#0891b2',
  '#7c3aed',
  '#16a34a',
  '#ef4444',
];
const OTHER_COLOR = '#94a3b8';
// Per-year line colors, newest first (current year = bold indigo).
const YEAR_COLORS = ['#4f46e5', '#0d9488', '#d97706', '#64748b', '#db2777', '#0891b2'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function shortSku(name: string): string {
  return (
    name
      .replace(/^Hooten Young\s*-?\s*/i, '')
      .replace(/\s*750ml.*$/i, '')
      .trim() || name
  );
}

interface StateProductTimelineProps {
  stateCode: string;
}

/**
 * Per-product year-over-year small multiples for one state: each product
 * gets its own mini chart (Jan–Dec on the x-axis, one line per year), the
 * same view as the Product Performance deep-dive, so you can compare the
 * same month across years for every product in the state.
 */
export function StateProductTimeline({ stateCode }: StateProductTimelineProps) {
  const { data, isLoading, error } = useStateProductTrend(stateCode);
  const products = useMemo(() => data?.products ?? [], [data?.products]);

  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());
  const [hiddenYears, setHiddenYears] = useState<Set<string>>(new Set());
  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Years present across the state, newest first (drives line color + order).
  const years = useMemo(() => {
    const s = new Set<string>();
    for (const m of data?.months ?? []) s.add(m.slice(0, 4));
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [data]);

  const colorByYear = useMemo(() => {
    const map = new Map<string, string>();
    years.forEach((y, i) => map.set(y, YEAR_COLORS[i % YEAR_COLORS.length]!));
    return map;
  }, [years]);

  const colorByProduct = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p, i) => {
      map.set(
        p.product_name,
        p.product_name === 'Other' ? OTHER_COLOR : PRODUCT_PALETTE[i % PRODUCT_PALETTE.length]!,
      );
    });
    return map;
  }, [products]);

  const visibleYears = useMemo(
    () => years.filter((y) => !hiddenYears.has(y)),
    [years, hiddenYears],
  );
  const visibleProducts = useMemo(
    () => products.filter((p) => !hiddenProducts.has(p.product_name)),
    [products, hiddenProducts],
  );

  if (error) {
    return (
      <Typography color="error" sx={{ fontSize: 12 }}>
        Failed to load product breakdown.
      </Typography>
    );
  }
  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (products.length === 0 || data.months.length === 0) {
    return (
      <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
        No product data for {stateCode}.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          {stateCode} — products, year over year
        </Typography>
        <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
          One chart per product · same month compared across years (6-pk)
        </Typography>
      </Box>

      {/* Filter bar — products (which panels) on top, year lines below. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 1.5,
          px: 1.75,
          py: 1.25,
          borderRadius: 2,
          border: `1px solid ${colors.borderLight}`,
          bgcolor: colors.surfaceSubtle,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <FilterLabel>Products</FilterLabel>
          {products.map((p) => (
            <ToggleChip
              key={p.product_name}
              label={shortSku(p.product_name)}
              color={colorByProduct.get(p.product_name)!}
              hidden={hiddenProducts.has(p.product_name)}
              onClick={() => toggle(setHiddenProducts, p.product_name)}
            />
          ))}
          {hiddenProducts.size > 0 && <ResetLink onClick={() => setHiddenProducts(new Set())} />}
        </Box>
        {years.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
              pt: 1,
              borderTop: `1px solid ${colors.borderLight}`,
            }}
          >
            <FilterLabel>Years</FilterLabel>
            {years.map((y) => (
              <ToggleChip
                key={y}
                label={y}
                color={colorByYear.get(y)!}
                hidden={hiddenYears.has(y)}
                onClick={() => toggle(setHiddenYears, y)}
              />
            ))}
          </Box>
        )}
      </Box>

      {visibleProducts.length === 0 || visibleYears.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: colors.textMuted, textAlign: 'center', py: 5 }}>
          Nothing selected — turn a product or year back on above.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {visibleProducts.map((p) => (
            <ProductYoyPanel
              key={p.product_name}
              name={p.product_name}
              productColor={colorByProduct.get(p.product_name)!}
              months={data.months}
              values={p.cases_9l.map((v) => Number(v))}
              years={years}
              visibleYears={visibleYears}
              colorByYear={colorByYear}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// One pivoted row per calendar month; each present year contributes a key.
interface YoyRow {
  month: string;
  vol: Record<string, number | null>;
}

interface ProductYoyPanelProps {
  name: string;
  productColor: string;
  months: string[];
  values: number[];
  years: string[];
  visibleYears: string[];
  colorByYear: Map<string, string>;
}

function ProductYoyPanel({
  name,
  productColor,
  months,
  values,
  years,
  visibleYears,
  colorByYear,
}: ProductYoyPanelProps) {
  const heroYear = years[0] ?? '';
  const total = values.reduce((a, b) => a + b, 0);

  // Pivot the full monthly history into 12 Jan–Dec rows, one value per year.
  const rows = useMemo<YoyRow[]>(() => {
    const out: YoyRow[] = MONTHS.map((m) => ({
      month: m,
      vol: Object.fromEntries(years.map((y) => [y, null])),
    }));
    months.forEach((iso, i) => {
      const y = iso.slice(0, 4);
      const mn = Number(iso.slice(5, 7));
      const row = out[mn - 1];
      if (row) row.vol[y] = values[i] ?? 0;
    });
    return out;
  }, [months, values, years]);

  const chartData = useMemo(() => rows.map((r) => ({ month: r.month, ...r.vol })), [rows]);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 1.5,
        p: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
        <Box
          sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: productColor, flexShrink: 0 }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={name}
        >
          {shortSku(name)}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: colors.textSecondary,
            ml: 'auto',
            fontFeatureSettings: '"tnum"',
          }}
        >
          {fmt6PkNumber(total)} 6-pk
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 9, fill: colors.textMuted }}
            axisLine={false}
            tickLine={false}
            interval={1}
          />
          <YAxis
            tick={{ fontSize: 9, fill: colors.textMuted }}
            axisLine={false}
            tickLine={false}
            width={30}
            tickFormatter={(v) => numberFormatter.format((v as number) * 2)}
          />
          <RTooltip
            cursor={{ stroke: colors.chartPrimary, strokeWidth: 1, strokeDasharray: '3 3' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <Box
                  sx={{
                    bgcolor: '#fff',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1.5,
                    p: 1,
                    boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
                    minWidth: 130,
                  }}
                >
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.4 }}>
                    {String(label)}
                  </Typography>
                  {[...payload]
                    .sort((a, b) => Number(b.dataKey) - Number(a.dataKey))
                    .map((e) => {
                      if (e.value == null) return null;
                      const key = String(e.dataKey);
                      return (
                        <Box
                          key={key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: colorByYear.get(key),
                              }}
                            />
                            <Typography
                              sx={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary }}
                            >
                              {key}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{ fontSize: 11, fontWeight: 700, fontFeatureSettings: '"tnum"' }}
                          >
                            {fmt6PkNumber(Number(e.value))} 6-pk
                          </Typography>
                        </Box>
                      );
                    })}
                </Box>
              );
            }}
          />
          {visibleYears.map((y) => (
            <Line
              key={y}
              type="monotone"
              dataKey={y}
              stroke={colorByYear.get(y)}
              strokeWidth={y === heroYear ? 2.25 : 1.5}
              strokeOpacity={y === heroYear ? 1 : 0.55}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

function FilterLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      {children}
    </Typography>
  );
}

function ResetLink({ onClick }: { onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 700,
        color: colors.chartPrimary,
        ml: 0.5,
        p: 0,
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      Show all
    </Box>
  );
}

interface ToggleChipProps {
  label: string;
  color: string;
  hidden: boolean;
  onClick: () => void;
}

function ToggleChip({ label, color, hidden, onClick }: ToggleChipProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1,
        py: 0.4,
        borderRadius: 999,
        cursor: 'pointer',
        border: `1px solid ${hidden ? colors.borderLight : colors.border}`,
        bgcolor: hidden ? 'transparent' : '#fff',
        boxShadow: hidden ? 'none' : '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover': { borderColor: colors.textMuted },
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: hidden ? 'transparent' : color,
          border: `2px solid ${color}`,
          opacity: hidden ? 0.5 : 1,
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: hidden ? colors.textMuted : colors.textPrimary,
          textDecoration: hidden ? 'line-through' : 'none',
        }}
      >
        {label}
      </Box>
    </Box>
  );
}
